import qs from 'query-string'
import { compose, setDisplayName, pure, withState, branch, withHandlers } from 'recompose'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { get } from 'lodash'

import { applicationStatuses } from 'utils/enums/feedbackStatuses'
import { validateRequiredField } from 'utils/helpers/validators/byType/requiredField'
import { validateSignature } from 'utils/helpers/validators/byType/signature'
import { validateYear } from 'utils/helpers/validators/byType/yearField'
import { validatePhone } from 'utils/helpers/validators/byType/phone'
import { handleShiftFields } from 'utils/helpers/form'
import { transformRequestPayload } from 'utils/helpers/applicationForm'
import { getFormConfigByType, getInitialValuesByType } from 'utils/helpers/formConfig'
import { withComponentDidMount } from 'hoc/withComponentDidMount'
import { withComponentDidUpdate } from 'hoc/withComponentDidUpdate'
import { withForm } from 'hoc/withForm'
import { getFormById } from 'store/entities/feedbackForms/FeedbackFormsSelectors'
import { fetchFeedbackFormById, submitFeedbackForm } from 'store/entities/feedbackForms/FeedbackFormsActions'
import { FeedbackView } from 'containers/pages/feedback/FeedbackView'

const mapStateToProps = (state, props) => {
  const { search } = props.history.location
  const { id } = qs.parse(search) || {}

  return {
    formId: id,
    form: getFormById(state, { id }),
  }
}

const mapDispatchToProps = dispatch => ({
  dispatchFetchForm: id => dispatch(fetchFeedbackFormById({ id })),
  dispatchSubmitForm: formData => dispatch(submitFeedbackForm({ formData })),
})

// Need to redirect user to submitted page if failed to load the form (incorrect id passed)
const handleComponentDidMount = ({ formId, setIsLoading, history, dispatchFetchForm }) => {
  setIsLoading(true)

  dispatchFetchForm(formId)
    .then(() => setIsLoading(false))
    .catch(() => {
      history.push('/submitted?type=NO_FORM')
    })
}

// Need to redirect user to submitted page if form status is other than "DRAFT"
const handleComponentDidUpdate = (prevProps, nextProps) => {
  const nextFormStatus = get(nextProps, 'form.status')

  if (nextFormStatus && nextFormStatus !== applicationStatuses.DRAFT) {
    nextProps.history.push('/submitted')
  }
}

const handleUploadScan =
  ({ handleFieldChange }) =>
  (fieldName, scansCount, scan) => {
    handleFieldChange(`${fieldName}${scansCount + 1}`)(scan)
  }

const handleDeleteScan =
  ({ handleShiftFields }) =>
  (fieldName, scansCount, deleteIndex) =>
    handleShiftFields({ fieldsNames: fieldName, index: deleteIndex })

const handleSubmitForm =
  ({ form, formData, history, dispatchSubmitForm, setIsSaving }) =>
  () => {
    const requestPayload = transformRequestPayload({ form, formData })

    setIsSaving(true)

    dispatchSubmitForm(requestPayload).finally(() => {
      history.push('/submitted?type=FROM_FEEDBACK')
    })
  }

export const FeedbackPage = compose(
  setDisplayName('FeedbackPage'),
  pure,
  withState('isLoading', 'setIsLoading', false),
  withState('isSaving', 'setIsSaving', false),
  withState('isFormBusy', 'setIsFormBusy', false),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withComponentDidUpdate(handleComponentDidUpdate),
  withComponentDidMount(handleComponentDidMount),
  branch(
    ({ isLoading }) => !isLoading,
    withForm(({ form }) => ({
      initialFormValues: getInitialValuesByType({ form }),
      formConfig: {
        // Applicant Fields:
        firstName: {},
        lastName: {},
        middleName: {},
        maidenName: {},
        birthday: {},
        ssn: {},

        // Employment Info Fields:
        employmentDistrictTitle: {
          validator: validateRequiredField,
          isRequired: true,
        },
        employmentPositionTitle: {
          validator: validateRequiredField,
          isRequired: true,
        },
        employmentFromMonth: {
          validator: validateRequiredField,
          isRequired: true,
        },
        employmentFromYear: {
          validator: validateRequiredField,
          isRequired: true,
        },
        employmentToMonth: {
          validator: validateRequiredField,
          isRequired: false,
        },
        employmentToYear: {
          validator: validateRequiredField,
          isRequired: false,
        },

        // Employer Fields:
        employerFirstName: {
          isTrimPayloadValue: true,
          validator: validateRequiredField,
          isRequired: true,
        },
        employerLastName: {
          isTrimPayloadValue: true,
          validator: validateRequiredField,
          isRequired: true,
        },
        employerEmail: {
          isTrimPayloadValue: true,
          validator: validateRequiredField,
          isRequired: true,
        },
        employerTitle: {
          isTrimPayloadValue: true,
          validator: validateRequiredField,
          isRequired: true,
        },
        employerPhone: {
          validator: validatePhone,
          isRequired: true,
        },

        // Checkboxes

        unknownPerson: {},
        employmentDatesOverride: {},

        // Employment Dates override

        employmentFromMonthOverride: {
          required: formData => !formData.unknownPerson.value && formData.employmentDatesOverride.value,
          validator: validateRequiredField,
        },
        employmentFromYearOverride: {
          required: formData => !formData.unknownPerson.value && formData.employmentDatesOverride.value,
          validator: validateYear,
        },
        employmentToMonthOverride: {
          required: formData =>
            !formData.unknownPerson.value &&
            !formData.employmentStillWorksHereOverride.value &&
            formData.employmentDatesOverride.value,
          validator: validateRequiredField,
        },
        employmentToYearOverride: {
          required: formData =>
            !formData.unknownPerson.value &&
            !formData.employmentStillWorksHereOverride.value &&
            formData.employmentDatesOverride.value,
          validator: validateYear,
        },
        employmentStillWorksHereOverride: {},

        // Feedback:

        ...getFormConfigByType({ form }),

        comment: {
          isTrimPayloadValue: true,
        },

        signature: {
          isRequired: true,
          validator: validateSignature,
        },

        timestamp: {
          validator: () => '',
        },
      },
    })),
  ),
  withHandlers({ handleShiftFields }),
  withHandlers({
    handleUploadScan,
    handleDeleteScan,
    handleSubmitForm,
  }),
)(FeedbackView)
